import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  ChatMeetingChoice,
  WaBriefingChatMeetingResponse,
} from '@dreamai/types';
import { applyUserLanguage } from '../i18n';
import {
  WA_BRIEFING_NOT_RELEVANT_OPTION_ID,
  WA_BRIEFING_TASK_DELEGATE_OPTION_ID,
  WA_BRIEFING_TASK_FIND_TIME_OPTION_ID,
  WA_BRIEFING_TASK_KEPT_OPTION_ID,
  type WaBriefingBookResponse,
  type WaBriefingContentData,
  type WaBriefingContentResponse,
  type WaBriefingDayPart,
  type WaBriefingDecideResponse,
  type WaBriefingFindTimeData,
  type WaBriefingFindTimeResponse,
  type WaBriefingFinishResponse,
  type WaBriefingItem,
  type WaBriefingOption,
  type WaBriefingReopenResponse,
  type WaBriefingTourResponse,
} from '../types/waBriefing';
import FeatureTour from './feature-tour/FeatureTour';
import type { TourTopic } from './feature-tour/tourScenes';
import DreamMarque from './common/DreamMarque';
import PublicNoticePage from './common/PublicNoticePage';
import MorningList from './morning-list/MorningList';
import useWaBriefingVoiceNote from './useWaBriefingVoiceNote';
import { ReactComponent as DreamWordmark } from '../icons/brand/dream-wordmark.svg';
import './wa-briefing-page.scss';

type PageState = 'loading' | 'ready' | 'expired' | 'invalid' | 'error';
type Screen = 'list' | 'after' | 'todos';
type Direction = 'forward' | 'back';

const TOAST_MS = 2800;

const DAY_PARTS: { id: WaBriefingDayPart; label: string }[] = [
  { id: 'morning', label: 'briefing.partMorning' },
  { id: 'after_lunch', label: 'briefing.partAfterLunch' },
  { id: 'afternoon', label: 'briefing.partAfternoon' },
  { id: 'end_of_day', label: 'briefing.partEndOfDay' },
];

const External: React.FC = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    aria-hidden='true'
  >
    <path d='M14 5h5v5M19 5l-8 8M18 14v5H5V6h5' />
  </svg>
);

const Chevron: React.FC = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    aria-hidden='true'
  >
    <path d='m7 10 5 5 5-5' />
  </svg>
);

const ChevronLeft: React.FC = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    aria-hidden='true'
  >
    <path d='m14 6-6 6 6 6' />
  </svg>
);

const Mic: React.FC = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinecap='round'
    aria-hidden='true'
  >
    <rect x='9' y='3' width='6' height='11' rx='3' />
    <path d='M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21' />
  </svg>
);

const Send: React.FC = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M12 19V5M5.5 11.5 12 5l6.5 6.5' />
  </svg>
);

/** The option a card opens on: the recommended one, else the first generated one. */
export function defaultOptionId(item: WaBriefingItem): string | null {
  if (item.decision) return item.decision.optionId;
  return (
    item.options.find((o) => o.recommended)?.id ??
    item.options.find((o) => !o.standing)?.id ??
    item.options[0]?.id ??
    null
  );
}

interface Props {
  /** A screen between the last card and the Morning List (used by /demo prototypes). */
  afterCards?: (proceed: () => void) => React.ReactNode;
  phone?: boolean;
}

const WaBriefingPage: React.FC<Props> = ({ afterCards, phone = false }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [data, setData] = useState<WaBriefingContentData | null>(null);
  const [screen, setScreen] = useState<Screen>('list');
  const [index, setIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [direction, setDirection] = useState<Direction>('forward');
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const leaveTimerRef = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [meetingActionId, setMeetingActionId] = useState<string | null>(null);
  const [meetingErrorId, setMeetingErrorId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [copiedDraftId, setCopiedDraftId] = useState<string | null>(null);
  const copiedDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [whyFor, setWhyFor] = useState<WaBriefingItem | null>(null);
  const [whyText, setWhyText] = useState('');
  // The card whose "hand it to …" note is open, and the note so far.
  const [delegatingId, setDelegatingId] = useState<string | null>(null);
  const [delegateNote, setDelegateNote] = useState('');
  const delegateNoteRef = useRef<HTMLInputElement>(null);
  // The card whose "find time" is open, the slot offered, and the part of
  // the day being looked in (null: the first free stretch anywhere).
  const [timingId, setTimingId] = useState<string | null>(null);
  const [timeOffer, setTimeOffer] = useState<WaBriefingFindTimeData | null>(
    null,
  );
  const [timePart, setTimePart] = useState<WaBriefingDayPart | null>(null);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const actionAreaRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const finishingRef = useRef(false);
  const finishFailedRef = useRef(false);
  // Decisions are applied on screen straight away and persisted behind the
  // card change; a card's in-flight request lives here until it settles.
  const pendingRef = useRef(new Map<string, Promise<boolean>>());
  const itemsRef = useRef<WaBriefingItem[]>([]);
  const decideSeqRef = useRef(0);

  const notify = useCallback((message: string) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const copyDraft = async (itemId: string, text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedDraftId(itemId);
      if (copiedDraftTimerRef.current) {
        clearTimeout(copiedDraftTimerRef.current);
      }
      copiedDraftTimerRef.current = setTimeout(
        () => setCopiedDraftId(null),
        2000,
      );
    } catch {
      setCopiedDraftId((current) => (current === itemId ? null : current));
    }
  };

  useEffect(
    () => () => {
      if (copiedDraftTimerRef.current) {
        clearTimeout(copiedDraftTimerRef.current);
      }
    },
    [],
  );

  const post = useCallback(
    async <T,>(path: string, body: Record<string, unknown>) => {
      const response = await fetch(`${apiBase}/wa-briefing/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...body }),
      });
      const payload: (T & { error?: string }) | null = await response
        .json()
        .catch(() => null);
      return { response, payload };
    },
    [apiBase, token],
  );

  const isTest = Boolean(data?.test);
  const items = useMemo(() => data?.items ?? [], [data]);
  itemsRef.current = items;
  const openItems = useMemo(
    () => items.filter((i) => i.status === 'open'),
    [items],
  );

  const patchItem = useCallback((next: WaBriefingItem) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((i) => (i.id === next.id ? next : i)),
          }
        : prev,
    );
  }, []);

  const handleMeetingChoice = useCallback(
    async (item: WaBriefingItem, choice: ChatMeetingChoice) => {
      if (meetingActionId) return;
      setMeetingActionId(item.id);
      setMeetingErrorId(null);
      try {
        const { response, payload } = await post<WaBriefingChatMeetingResponse>(
          'chat-meeting',
          {
            itemId: item.id,
            choice,
          },
        );
        if (!response.ok || !payload?.success || !payload.item) {
          throw new Error(
            payload?.error ?? 'Unable to save chat meeting choice',
          );
        }
        patchItem({ ...item, ...payload.item });
      } catch {
        setMeetingErrorId(item.id);
      } finally {
        setMeetingActionId(null);
      }
    },
    [meetingActionId, patchItem, post],
  );

  const finish = useCallback(async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    setBusy(true);
    setDirection('forward');
    setScreen('todos');
    window.scrollTo({ top: 0 });
    try {
      const settled = await Promise.all(pendingRef.current.values());
      if (settled.some((ok) => !ok)) {
        setScreen('list');
        return;
      }
      if (isTest) {
        setData((prev) => (prev ? { ...prev, finished: true } : prev));
        return;
      }
      const { response, payload } = await post<WaBriefingFinishResponse>(
        'finish',
        {},
      );
      if (response.status === 410) {
        setPageState('expired');
        return;
      }
      if (!response.ok || !payload?.data) {
        finishFailedRef.current = true;
        setScreen('list');
        notify(payload?.error ?? t('briefing.errorBody'));
        return;
      }
      const finished = payload.data;
      setData((prev) =>
        prev
          ? {
              ...prev,
              todos: finished.todos,
              plan: finished.plan,
              finished: true,
              whatsappUrl: finished.whatsappUrl ?? prev.whatsappUrl,
            }
          : prev,
      );
    } catch {
      finishFailedRef.current = true;
      setScreen('list');
      notify(t('briefing.errorBody'));
    } finally {
      finishingRef.current = false;
      setBusy(false);
    }
  }, [isTest, notify, post, t]);

  // The feature tour (people who joined before the new onboarding) plays
  // between the cards and the list; "Show me after" puts a to-do on Today.
  const tourLive = !!data?.tour && !data.tour.finished;
  const saveTour = useCallback(
    async (topics: TourTopic[], finished: boolean) => {
      try {
        const { response, payload } = await post<WaBriefingTourResponse>(
          'tour',
          { topics, finished },
        );
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok || !payload?.data) {
          notify(payload?.error ?? t('briefing.errorBody'));
          return;
        }
        const saved = payload.data;
        setData((prev) =>
          prev ? { ...prev, tour: saved.tour, todos: saved.todos } : prev,
        );
      } catch {
        notify(t('briefing.errorBody'));
      }
    },
    [notify, post, t],
  );
  const finishTour = useCallback(
    async (topics: TourTopic[]) => {
      await saveTour(topics, true);
      void finish();
    },
    [saveTour, finish],
  );

  // Initial load.
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${apiBase}/wa-briefing/content?token=${encodeURIComponent(token)}`,
        );
        const payload: WaBriefingContentResponse | null = await response
          .json()
          .catch(() => null);
        if (cancelled) return;
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok || !payload?.data) {
          setPageState(response.status === 404 ? 'invalid' : 'error');
          return;
        }
        applyUserLanguage(payload.data.language);
        setData(payload.data);
        const firstOpen = payload.data.items.findIndex(
          (i) => i.status === 'open',
        );
        setIndex(Math.max(firstOpen, 0));
        if (payload.data.finished) setScreen('todos');
        setPageState('ready');
      } catch {
        if (!cancelled) setPageState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  // Others can tick off or re-date shared to-dos; refresh on coming back.
  useEffect(() => {
    if (!token || screen !== 'todos') return;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const response = await fetch(
          `${apiBase}/wa-briefing/content?token=${encodeURIComponent(token)}`,
        );
        if (!response.ok) return;
        const payload: WaBriefingContentResponse | null = await response
          .json()
          .catch(() => null);
        const fresh = payload?.data;
        if (!fresh) return;
        setData((prev) =>
          prev ? { ...prev, todos: fresh.todos, lists: fresh.lists } : prev,
        );
      } catch {
        // Keep what's on screen.
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    // No live push on this page: re-read what the assistant is doing now and then.
    const poll = window.setInterval(onVisible, 2 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.clearInterval(poll);
    };
  }, [apiBase, token, screen]);

  // Let the last card fade out before the between-screen (demo prototypes).
  const toAfter = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    leaveTimerRef.current = window.setTimeout(() => setScreen('after'), 460);
  }, []);

  // A decision on the last card failed after the tour started opening: bring
  // the card back (as `finish` does for the to-do view) so it can be retried.
  const backFromAfter = useCallback(() => {
    if (!leavingRef.current) return;
    if (leaveTimerRef.current) window.clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = null;
    leavingRef.current = false;
    setLeaving(false);
    setScreen('list');
  }, []);

  // Everything already decided but the list was never finished (e.g. a reload
  // between the last tap and the finish): finish it now. A failed finish is
  // retried from the card's button, not from here.
  useEffect(() => {
    if (pageState !== 'ready' || !data || screen !== 'list') return;
    if (finishFailedRef.current) return;
    if (openItems.length === 0 && !data.finished) {
      if (afterCards || tourLive) toAfter();
      else void finish();
    }
  }, [
    pageState,
    data,
    screen,
    openItems.length,
    finish,
    afterCards,
    tourLive,
    toAfter,
  ]);

  useEffect(() => {
    setMenuOpen(false);
    titleRef.current?.focus({ preventScroll: true });
  }, [index, screen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        !actionAreaRef.current?.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const advance = useCallback(
    (from: number, nextItems: WaBriefingItem[]) => {
      const after = nextItems.findIndex(
        (item, position) => position > from && item.status === 'open',
      );
      const next =
        after >= 0
          ? after
          : nextItems.findIndex((item) => item.status === 'open');
      if (next < 0) {
        if (afterCards || tourLive) toAfter();
        else void finish();
      } else {
        setIndex(next);
      }
    },
    [finish, afterCards, tourLive, toAfter],
  );

  const persistDecision = useCallback(
    async (
      item: WaBriefingItem,
      optionId: string,
      note: string | null,
      seq: number,
    ) => {
      if (isTest) return true;
      try {
        if (item.decision) {
          const reopened = await post<WaBriefingReopenResponse>('reopen', {
            itemId: item.id,
          });
          if (reopened.response.status === 410) {
            setPageState('expired');
            return false;
          }
          if (!reopened.response.ok) {
            notify(reopened.payload?.error ?? t('briefing.errorBody'));
            return false;
          }
        }
        const { response, payload } = await post<WaBriefingDecideResponse>(
          'decide',
          { itemId: item.id, optionId, ...(note ? { note } : {}) },
        );
        if (response.status === 410) {
          setPageState('expired');
          return false;
        }
        if (!response.ok || !payload?.data) {
          notify(payload?.error ?? t('briefing.errorBody'));
          return false;
        }
        const decided = payload.data;
        patchItem(decided.item);
        if (seq === decideSeqRef.current) {
          setData((prev) =>
            prev ? { ...prev, todos: decided.todos, plan: decided.plan } : prev,
          );
        }
        if (decided.askWhy) {
          setWhyFor(decided.item);
          setWhyText('');
        }
        return true;
      } catch {
        notify(t('briefing.errorBody'));
        return false;
      }
    },
    [isTest, notify, patchItem, post, t],
  );

  const decide = useCallback(
    (item: WaBriefingItem, optionId: string, note: string | null = null) => {
      if (pendingRef.current.has(item.id)) return;
      const option = item.options.find((o) => o.id === optionId);
      if (!option) return;
      setMenuOpen(false);
      setDelegatingId(null);
      setDelegateNote('');
      setTimingId(null);
      setTimeOffer(null);

      const optimistic: WaBriefingItem = {
        ...item,
        status: 'closed',
        decision: {
          optionId: option.id,
          label: option.label,
          effect: option.effect,
          note,
          locked: false,
        },
      };
      const nextItems = itemsRef.current.map((i) =>
        i.id === item.id ? optimistic : i,
      );
      itemsRef.current = nextItems;
      setData((prev) => (prev ? { ...prev, items: nextItems } : prev));
      setPendingIds((prev) => [...prev, item.id]);

      const seq = ++decideSeqRef.current;
      const request = persistDecision(item, optionId, note, seq).then((ok) => {
        if (!ok) {
          patchItem(item);
          backFromAfter();
          setDirection('back');
          setIndex(itemsRef.current.findIndex((i) => i.id === item.id));
        }
        pendingRef.current.delete(item.id);
        setPendingIds((prev) => prev.filter((id) => id !== item.id));
        return ok;
      });
      pendingRef.current.set(item.id, request);

      setDirection('forward');
      advance(index, nextItems);
    },
    [advance, backFromAfter, index, patchItem, persistDecision],
  );

  /**
   * Tapping an option: "hand it to …" opens a note first (typed or spoken —
   * the brief for the assistant); everything else decides straight away.
   */
  const findTime = useCallback(
    async (
      item: WaBriefingItem,
      within: WaBriefingDayPart | null,
      after?: string,
    ) => {
      if (!item.todoId) return;
      setBusy(true);
      if (!within) setTimeOffer(null);
      try {
        const { response, payload } = await post<WaBriefingFindTimeResponse>(
          'find-time',
          within
            ? { todoId: item.todoId, part: within, after }
            : { todoId: item.todoId },
        );
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok || !payload?.data) {
          notify(payload?.error ?? t('briefing.errorBody'));
          setTimingId(null);
          return;
        }
        setTimeOffer(payload.data);
      } catch {
        notify(t('briefing.errorBody'));
        setTimingId(null);
      } finally {
        setBusy(false);
      }
    },
    [notify, post, t],
  );

  // Tapping the part already chosen moves on to its next free stretch.
  const choosePart = useCallback(
    (item: WaBriefingItem, next: WaBriefingDayPart) => {
      const after = timePart === next ? timeOffer?.slot?.start : undefined;
      setTimePart(next);
      void findTime(item, next, after);
    },
    [findTime, timeOffer, timePart],
  );

  // Booked a time for it: it is on for today, so the card closes as kept.
  const bookTime = useCallback(
    async (item: WaBriefingItem) => {
      const slot = timeOffer?.slot;
      if (!slot || !item.todoId || busy) return;
      setBusy(true);
      try {
        const { response, payload } = await post<WaBriefingBookResponse>(
          'book',
          { todoId: item.todoId, start: slot.start },
        );
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok || !payload?.data) {
          notify(payload?.error ?? t('briefing.errorBody'));
          return;
        }
        const { todos } = payload.data;
        setData((prev) => (prev ? { ...prev, todos } : prev));
        notify(t('briefing.booked', { time: slot.time }));
        setTimingId(null);
        setTimeOffer(null);
        if (
          item.options.some((o) => o.id === WA_BRIEFING_TASK_KEPT_OPTION_ID)
        ) {
          decide(item, WA_BRIEFING_TASK_KEPT_OPTION_ID);
        }
      } catch {
        notify(t('briefing.errorBody'));
      } finally {
        setBusy(false);
      }
    },
    [busy, decide, notify, post, t, timeOffer],
  );

  const choose = useCallback(
    (item: WaBriefingItem, optionId: string) => {
      if (optionId === WA_BRIEFING_TASK_DELEGATE_OPTION_ID) {
        setMenuOpen(false);
        setTimingId(null);
        setTimeOffer(null);
        setDelegatingId((open) => (open === item.id ? null : item.id));
        setDelegateNote('');
        return;
      }
      if (optionId === WA_BRIEFING_TASK_FIND_TIME_OPTION_ID) {
        setMenuOpen(false);
        setDelegatingId(null);
        setDelegateNote('');
        if (timingId === item.id) {
          setTimingId(null);
          setTimeOffer(null);
          return;
        }
        setTimingId(item.id);
        setTimePart(null);
        void findTime(item, null);
        return;
      }
      decide(item, optionId);
    },
    [decide, findTime, timingId],
  );

  const voice = useWaBriefingVoiceNote({
    token: token ?? '',
    apiBase,
    notify,
    onExpired: () => setPageState('expired'),
    onText: (text) => {
      setDelegateNote((prev) => [prev.trim(), text].filter(Boolean).join(' '));
      delegateNoteRef.current?.focus();
    },
  });

  const sendWhy = useCallback(async () => {
    if (!whyFor) return;
    const reason = whyText.trim();
    const item = whyFor;
    setWhyFor(null);
    if (!reason || isTest) return;
    try {
      const { response } = await post('why', { itemId: item.id, reason });
      if (response.ok) notify(t('briefing.whyThanks'));
    } catch {
      // A missed learning signal is not worth interrupting the list for.
    }
  }, [isTest, notify, post, t, whyFor, whyText]);

  const goPrevious = useCallback(() => {
    if (index > 0) {
      setDirection('back');
      setIndex(index - 1);
    }
  }, [index]);

  if (pageState === 'loading') {
    return <PublicNoticePage loading />;
  }

  if (pageState !== 'ready' || !data) {
    const expired = pageState === 'expired';
    return (
      <PublicNoticePage
        title={
          expired
            ? t('briefing.linkExpiredTitle')
            : t('briefing.somethingOffTitle')
        }
        body={
          expired
            ? t('briefing.expiredBody')
            : pageState === 'invalid'
              ? t('briefing.invalidBody')
              : t('briefing.errorBody')
        }
        testId='wa-briefing-notice'
      />
    );
  }

  const renderCard = () => {
    const item = items[index];
    if (!item) return null;
    const held = busy || pendingIds.includes(item.id);
    const locked = item.decision?.locked ?? false;
    const shown = item.options.filter(
      (o) => o.id !== WA_BRIEFING_NOT_RELEVANT_OPTION_ID,
    );
    // A to-do's card also offers to find it a time today, as the to-do view
    // does: with the outcomes, ahead of the standing options.
    const findTimeOption: WaBriefingOption | null = item.todoId
      ? {
          id: WA_BRIEFING_TASK_FIND_TIME_OPTION_ID,
          label: t('briefing.findTime'),
          effect: 'commit',
          recommended: false,
          standing: false,
        }
      : null;
    const standingFrom = shown.findIndex((o) => o.standing);
    const menuOptions = findTimeOption
      ? standingFrom < 0
        ? [...shown, findTimeOption]
        : [
            ...shown.slice(0, standingFrom),
            findTimeOption,
            ...shown.slice(standingFrom),
          ]
      : shown;
    const firstStanding = menuOptions.findIndex((o) => o.standing);
    const hasNotRelevant = item.options.some(
      (o) => o.id === WA_BRIEFING_NOT_RELEVANT_OPTION_ID,
    );
    const isPromotion =
      item.isPromotion === true || item.link?.kind === 'promotion';
    const chatDraftLink =
      item.link?.kind === 'linkedin_draft' ||
      item.link?.kind === 'whatsapp_draft'
        ? item.link
        : null;
    const meetingPending =
      item.status === 'open' && chatDraftLink?.meetingDecision === 'pending';
    const chatDraftText =
      item.status === 'open' && !meetingPending
        ? chatDraftLink?.draftText
        : undefined;
    const meetingName = (item.whoLabel ?? item.headline)
      .split(/[ ·,]/)[0]
      .trim();
    const delegating = delegatingId === item.id;
    const timing = timingId === item.id;
    const selectedId = defaultOptionId(item);
    const selected =
      timing && findTimeOption
        ? findTimeOption
        : (item.options.find((o) => o.id === selectedId) ?? item.options[0]);
    const assistant = data?.assistantName ?? t('briefing.assistantFallback');

    return (
      <section
        className='wa-briefing__list'
        aria-label={t('briefing.listAria')}
      >
        <article
          key={item.id}
          className={`wa-briefing__card wa-briefing__card--${direction}${
            leaving ? ' wa-briefing__card--leaving' : ''
          }`}
          data-testid='wa-briefing-card'
          data-item-id={item.id}
          aria-labelledby='wa-briefing-card-title'
        >
          {item.image && (
            <img className='wa-briefing__image' src={item.image.url} alt='' />
          )}
          {item.whoLabel && (
            <p className='wa-briefing__who' data-testid='wa-briefing-who'>
              {item.whoLabel}
            </p>
          )}
          <h1 id='wa-briefing-card-title' ref={titleRef} tabIndex={-1}>
            {item.headline}
          </h1>
          <p className='wa-briefing__context'>{item.context}</p>
          {chatDraftLink && (chatDraftText || meetingPending) && (
            <div className='wa-briefing__chat-draft'>
              {meetingPending ? (
                <div className='wa-briefing__chat-meeting'>
                  <p>{t('chatMeeting.prompt', { name: meetingName })}</p>
                  <div className='wa-briefing__chat-meeting-actions'>
                    <button
                      type='button'
                      disabled={meetingActionId !== null}
                      onClick={() => void handleMeetingChoice(item, 'meet')}
                    >
                      {t('chatMeeting.yes')}
                    </button>
                    <button
                      type='button'
                      disabled={meetingActionId !== null}
                      onClick={() => void handleMeetingChoice(item, 'decline')}
                    >
                      {t('chatMeeting.decline')}
                    </button>
                    <button
                      type='button'
                      disabled={meetingActionId !== null}
                      onClick={() => void handleMeetingChoice(item, 'ignore')}
                    >
                      {t('chatMeeting.notInterested')}
                    </button>
                  </div>
                  {meetingErrorId === item.id && (
                    <p className='wa-briefing__chat-meeting-error' role='alert'>
                      {t('chatMeeting.error')}
                    </p>
                  )}
                </div>
              ) : (
                <blockquote
                  className='wa-briefing__draft'
                  data-testid='wa-briefing-draft'
                >
                  {chatDraftText}
                </blockquote>
              )}
              <a
                className='wa-briefing__draft-link'
                data-testid='wa-briefing-draft-link'
                href={chatDraftLink.url}
                target='_blank'
                rel='noopener noreferrer'
              >
                {chatDraftLink.kind === 'linkedin_draft'
                  ? t('briefing.openLinkedin')
                  : t('briefing.openWhatsapp')}
              </a>
              {!meetingPending &&
                chatDraftLink.kind === 'whatsapp_draft' &&
                chatDraftLink.delivery === 'in_app' && (
                  <p className='wa-briefing__draft-note'>
                    {t('briefing.draftOnMac')}
                  </p>
                )}
            </div>
          )}
          {item.decision && (
            <p className='wa-briefing__chosen' data-testid='wa-briefing-chosen'>
              {t('briefing.chosen', { label: item.decision.label })}
            </p>
          )}

          <div className='wa-briefing__action-area' ref={actionAreaRef}>
            <div className='wa-briefing__action-row'>
              {locked ? (
                <button
                  type='button'
                  className='wa-briefing__primary'
                  onClick={() => {
                    setDirection('forward');
                    advance(index, items);
                  }}
                  disabled={held}
                >
                  {openItems.length
                    ? t('briefing.next')
                    : t('briefing.seeList')}
                </button>
              ) : (
                <>
                  {!meetingPending && (
                    <div className='wa-briefing__split'>
                      {item.link ? (
                        chatDraftText ? (
                          <button
                            type='button'
                            className='wa-briefing__apply'
                            data-testid='wa-briefing-copy-draft'
                            onClick={() =>
                              void copyDraft(item.id, chatDraftText)
                            }
                          >
                            {copiedDraftId === item.id
                              ? t('briefing.copied')
                              : t('briefing.copyDraft')}
                          </button>
                        ) : (
                          <a
                            className='wa-briefing__apply wa-briefing__apply--link'
                            data-testid='wa-briefing-link'
                            href={item.link.url}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            {item.link.kind === 'promotion'
                              ? item.link.label
                              : item.link.kind === 'linkedin_draft'
                                ? t('briefing.openLinkedin')
                                : item.link.kind === 'whatsapp_draft'
                                  ? t('briefing.openWhatsapp')
                                  : t('briefing.openDraft')}
                            {item.link.kind === 'draft' && <External />}
                          </a>
                        )
                      ) : (
                        <button
                          type='button'
                          className='wa-briefing__apply'
                          data-testid='wa-briefing-apply'
                          disabled={held || !selected}
                          onClick={() => {
                            if (!selected) return;
                            if (timing) {
                              setTimePart(null);
                              void findTime(item, null);
                              return;
                            }
                            choose(item, selected.id);
                          }}
                        >
                          {selected?.label}
                        </button>
                      )}
                      {!isPromotion && (
                        <button
                          type='button'
                          className='wa-briefing__toggle'
                          aria-label={t('briefing.chooseAnother')}
                          aria-expanded={menuOpen}
                          aria-controls={`options-${item.id}`}
                          disabled={held}
                          onClick={() => setMenuOpen((open) => !open)}
                        >
                          <Chevron />
                        </button>
                      )}
                    </div>
                  )}
                  {isPromotion && (
                    <button
                      type='button'
                      className='wa-briefing__option'
                      data-testid='wa-briefing-promotion-decline'
                      disabled={held}
                      onClick={() => choose(item, 'not_relevant')}
                    >
                      {
                        item.options.find(
                          (option) => option.id === 'not_relevant',
                        )?.label
                      }
                    </button>
                  )}
                  {menuOpen && !isPromotion && (
                    <div
                      className='wa-briefing__options'
                      id={`options-${item.id}`}
                      role='group'
                      aria-label={t('briefing.optionsAria')}
                    >
                      {menuOptions.map((option, position) => (
                        <React.Fragment key={option.id}>
                          {position === firstStanding && position > 0 && (
                            <div
                              className='wa-briefing__option-divider'
                              aria-hidden='true'
                            />
                          )}
                          <button
                            type='button'
                            className='wa-briefing__option'
                            aria-pressed={
                              !item.link && option.id === selected?.id
                            }
                            disabled={held}
                            onClick={() => choose(item, option.id)}
                          >
                            {option.label}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {delegating && (
                    <form
                      className={`wa-briefing__say${voice.recording ? ' wa-briefing__say--recording' : ''}`}
                      data-testid='wa-briefing-delegate-form'
                      onSubmit={(event) => {
                        event.preventDefault();
                        decide(
                          item,
                          WA_BRIEFING_TASK_DELEGATE_OPTION_ID,
                          delegateNote.trim() || null,
                        );
                      }}
                    >
                      {voice.recording ? (
                        <span
                          className='wa-briefing__listening'
                          aria-live='polite'
                        >
                          {t('briefing.listening')}
                        </span>
                      ) : (
                        <input
                          ref={delegateNoteRef}
                          type='text'
                          value={delegateNote}
                          onChange={(event) =>
                            setDelegateNote(event.target.value)
                          }
                          placeholder={
                            voice.transcribing
                              ? t('briefing.transcribing')
                              : t('briefing.notePlaceholder')
                          }
                          aria-label={t('briefing.noteAria', {
                            name: assistant,
                            title: item.headline,
                          })}
                          autoComplete='off'
                          autoFocus
                          disabled={voice.transcribing}
                        />
                      )}
                      <button
                        type='button'
                        className='wa-briefing__mic'
                        aria-label={
                          voice.recording
                            ? t('briefing.stopRecording')
                            : t('briefing.recordVoiceNote')
                        }
                        aria-pressed={voice.recording}
                        disabled={voice.transcribing}
                        onClick={() => void voice.toggleRecording()}
                      >
                        {voice.recording ? (
                          <span className='wa-briefing__stop' />
                        ) : (
                          <Mic />
                        )}
                      </button>
                      {!voice.recording && (
                        <button
                          type='submit'
                          className='wa-briefing__send'
                          aria-label={t('briefing.delegateSend', {
                            name: assistant,
                          })}
                          disabled={held || voice.transcribing}
                        >
                          <Send />
                        </button>
                      )}
                    </form>
                  )}
                  {timing && (
                    <div
                      className='wa-briefing__offer'
                      data-testid='wa-briefing-time-offer'
                      aria-live='polite'
                    >
                      {!timeOffer ? (
                        <p>{t('briefing.lookingForTime')}</p>
                      ) : timeOffer.slot ? (
                        <div className='wa-briefing__offer-head'>
                          <p>
                            {timeOffer.context
                              ? t('briefing.howAboutContext', {
                                  time: timeOffer.slot.time,
                                  context: timeOffer.context,
                                })
                              : t('briefing.howAbout', {
                                  time: timeOffer.slot.time,
                                })}
                          </p>
                          <button
                            type='button'
                            className='wa-briefing__book'
                            disabled={held}
                            onClick={() => void bookTime(item)}
                          >
                            {t('briefing.bookIt')}
                          </button>
                        </div>
                      ) : (
                        <p>
                          {timeOffer.reason === 'no_calendar'
                            ? t('briefing.noCalendar')
                            : timeOffer.reason === 'no_free_time'
                              ? t(
                                  timePart
                                    ? 'briefing.noFreeTimeInPart'
                                    : 'briefing.noFreeTime',
                                )
                              : t('briefing.calendarUnavailable')}
                        </p>
                      )}
                      {!!timeOffer?.parts?.length && (
                        <div
                          className='wa-briefing__parts'
                          role='group'
                          aria-label={t('briefing.otherTimes')}
                        >
                          {DAY_PARTS.map(({ id, label }) => (
                            <button
                              key={id}
                              type='button'
                              className='wa-briefing__part'
                              aria-pressed={timePart === id}
                              disabled={held || !timeOffer.parts.includes(id)}
                              onClick={() => choosePart(item, id)}
                            >
                              {t(label)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {hasNotRelevant && !isPromotion && (
                    <button
                      type='button'
                      className='wa-briefing__dismiss'
                      data-testid='wa-briefing-dismiss'
                      aria-label={t('briefing.notRelevantAria')}
                      disabled={held}
                      onClick={() =>
                        decide(item, WA_BRIEFING_NOT_RELEVANT_OPTION_ID)
                      }
                    >
                      {t('briefing.notRelevant')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </article>

        <nav className='wa-briefing__nav' aria-label={t('briefing.navAria')}>
          <button
            type='button'
            className='wa-briefing__icon-button'
            aria-label={t('briefing.previous')}
            onClick={goPrevious}
            disabled={index === 0}
          >
            <ChevronLeft />
          </button>
          <span
            className='wa-briefing__counter'
            data-testid='wa-briefing-counter'
          >
            {index + 1} / {items.length}
          </span>
          <span aria-hidden='true' />
        </nav>

        {whyFor && (
          <div className='wa-briefing__why' role='dialog' aria-live='polite'>
            <p>{t('briefing.whyPrompt', { headline: whyFor.headline })}</p>
            <input
              type='text'
              value={whyText}
              onChange={(event) => setWhyText(event.target.value)}
              placeholder={t('briefing.whyPlaceholder')}
              aria-label={t('briefing.whyPlaceholder')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void sendWhy();
              }}
            />
            <div className='wa-briefing__why-actions'>
              <button
                type='button'
                className='wa-briefing__text-button'
                onClick={() => setWhyFor(null)}
              >
                {t('briefing.whySkip')}
              </button>
              <button
                type='button'
                className='wa-briefing__text-button wa-briefing__text-button--strong'
                onClick={() => void sendWhy()}
                disabled={!whyText.trim()}
              >
                {t('briefing.whySend')}
              </button>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderTodos = () =>
    token ? (
      <MorningList
        data={data}
        update={(patch) =>
          setData((prev) => (prev ? { ...prev, ...patch } : prev))
        }
        post={async (path, body) => {
          if (isTest) {
            return {
              ok: false,
              status: 403,
              payload: {
                error: 'This is a test list: changes aren’t saved.',
              } as never,
            };
          }
          const { response, payload } = await post(path, body);
          return {
            ok: response.ok,
            status: response.status,
            payload: payload as never,
          };
        }}
        onExpired={() => setPageState('expired')}
        phone={phone}
      />
    ) : null;

  if (screen === 'todos') return renderTodos();

  const showTour = screen === 'after' && !afterCards && tourLive && data?.tour;

  return (
    <div
      className={`wa-briefing${showTour ? ' wa-briefing--tour' : ''}${phone ? ' wa-briefing--phone' : ''}`}
      data-testid='wa-briefing'
    >
      <main className='wa-briefing__app'>
        <header
          className='wa-briefing__brand'
          aria-label={t('briefing.brandAria')}
        >
          <DreamMarque className='wa-briefing__marque' aria-hidden='true' />
          <DreamWordmark className='wa-briefing__wordmark' aria-hidden='true' />
        </header>
        {screen === 'after' && afterCards
          ? afterCards(() => void finish())
          : showTour
            ? null
            : renderCard()}
      </main>
      {showTour && data.tour && (
        <FeatureTour
          ending={data.tour.ending}
          assistantName={data.assistantName}
          queued={data.tour.topics.map((topic) => topic.id)}
          context='briefing'
          onQueue={(topics) => void saveTour(topics, false)}
          onDone={(topics) => void finishTour(topics)}
        />
      )}
      {toast && (
        <div className='wa-briefing__status' role='status'>
          {toast}
        </div>
      )}
    </div>
  );
};

export default WaBriefingPage;
